import type { PlayerReport } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { da } from '../../i18n/da';
import { fetchReports, markReportSeen } from '../auth/auth-api';
import styles from './ChatLogPanel.module.css';

const time = new Intl.DateTimeFormat('da-DK', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * For the admin: the children's reports of other players, with what the reported player wrote
 * just before (ADR 0018). Blocking happens in the panel above.
 */
export function ReportsPanel() {
  const [reports, setReports] = useState<PlayerReport[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the refresh button to load the reports again.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const result = await fetchReports();
      if (!active) return;
      setFailed(!result.ok);
      if (result.ok) setReports(result.data.reports);
    };
    void load();
    return () => {
      active = false;
    };
  }, [version]);

  const markSeen = async (id: string) => {
    const result = await markReportSeen(id);
    if (result.ok) {
      setReports(
        (current) =>
          current?.map((report) => (report.id === id ? { ...report, seen: true } : report)) ?? null,
      );
    }
  };

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <strong>{da.admin.reportsTitle}</strong>
        <Button size="small" variant="quiet" onClick={() => setVersion((value) => value + 1)}>
          {da.admin.chatRefresh}
        </Button>
      </div>
      {failed && <p className={styles.note}>{da.admin.reportsLoadFailed}</p>}
      {reports?.length === 0 && <p className={styles.note}>{da.admin.reportsEmpty}</p>}
      {reports && reports.length > 0 && (
        <ul className={styles.list} data-testid="reports">
          {reports.map((report) => (
            <li key={report.id} className={report.seen ? undefined : styles.stopped}>
              <span className={styles.meta}>
                {time.format(report.createdAt)} · {report.room} · {report.reporter.username}
              </span>
              <strong>
                {da.admin.reportLine(report.reporter.displayName, report.reported.displayName)}
              </strong>
              {report.recentChat.length === 0 && (
                <span className={styles.meta}>{da.admin.reportNoChat}</span>
              )}
              {report.recentChat.map((line) => (
                <span key={line.sentAt}>
                  {line.rejected && <em className={styles.tag}>{da.admin.chatStopped}</em>}{' '}
                  {line.text}
                </span>
              ))}
              {report.seen ? (
                <span className={styles.meta}>{da.admin.reportSeen}</span>
              ) : (
                <span>
                  <Button size="small" variant="quiet" onClick={() => void markSeen(report.id)}>
                    {da.admin.reportMarkSeen}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
